import { format } from 'date-fns';

import { Badge } from '@apps/web/components/ui/badge';
import { Card, CardContent, CardHeader } from '@apps/web/components/ui/card';
import { formatDelimitedLabel } from '@apps/web/lib/format_utils';
import { type CaregiverAgentJudgmentRead } from '@libraries/typescript/api_client';

function verdictVariant(
    verdict: CaregiverAgentJudgmentRead['verdict'],
): 'destructive' | 'outline' | 'secondary' {
    if (verdict === 'BAD') {
        return 'destructive';
    }
    if (verdict === 'GOOD') {
        return 'secondary';
    }
    return 'outline';
}

export function CaregiverAgentJudgmentsPanel({
    judgments,
}: {
    readonly judgments: ReadonlyArray<CaregiverAgentJudgmentRead>;
}) {
    if (judgments.length === 0) {
        return (
            <div className="text-muted-foreground rounded-xl border p-6 text-center text-sm">
                No caregiver-agent judgments yet.
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {judgments.map((judgment, index) => (
                <Card key={`${judgment.run_started_event_id}:${index}`}>
                    <CardHeader className="flex flex-row flex-wrap items-center gap-2 pb-3">
                        <Badge variant={verdictVariant(judgment.verdict)}>
                            {formatDelimitedLabel(judgment.verdict)}
                        </Badge>
                        <Badge variant="outline">
                            {formatDelimitedLabel(judgment.severity)} severity
                        </Badge>
                        <span className="text-muted-foreground text-xs">
                            Judged{' '}
                            {format(
                                new Date(judgment.judged_at),
                                'MMM d, h:mm a',
                            )}
                            {judgment.judge_model
                                ? ` by ${judgment.judge_model}`
                                : null}
                        </span>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm">{judgment.summary}</p>
                        {judgment.issue_buckets.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {judgment.issue_buckets.map((bucket) => (
                                    <Badge key={bucket} variant="secondary">
                                        {formatDelimitedLabel(bucket)}
                                    </Badge>
                                ))}
                            </div>
                        ) : null}
                        {judgment.evidence.length > 0 ? (
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium">
                                    Evidence
                                </h3>
                                <ul className="space-y-2">
                                    {judgment.evidence.map(
                                        (evidence, evidenceIndex) => (
                                            <li
                                                key={`${evidence.source}:${evidenceIndex}`}
                                                className="bg-muted rounded-md p-3 text-sm"
                                            >
                                                <span className="text-muted-foreground mr-2 text-xs font-medium uppercase">
                                                    {evidence.source}
                                                </span>
                                                {evidence.quote}
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </div>
                        ) : null}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
